import type React from 'react';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useRef,
  useState,
} from 'react';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { ScreenerAiFilterSearchResponse } from '../ai-search/screener-ai-search-api-request';
import type { FilterId } from '../types/filters';

export type AiSearchState = {
  prompt: string;
  uuid: string;
  isCorrect: boolean | null;
  response:
    | TalentDiscoveryAiFilterSearchResponseV2
    | ScreenerAiFilterSearchResponse
    | null;
};

interface AiSearchContextType {
  // AI search state
  aiSearchState: AiSearchState;
  setAiSearchState: React.Dispatch<React.SetStateAction<AiSearchState>>;

  // Loading state
  isAiSearchLoading: boolean;
  setIsAiSearchLoading: React.Dispatch<React.SetStateAction<boolean>>;

  // Abort controller for cancelling requests
  abortControllerRef: React.MutableRefObject<AbortController | undefined>;

  // AI-generated filter tracking (for UI highlighting only)
  aiGeneratedFilterIds: FilterId[];
  setAiGeneratedFilterIds: React.Dispatch<React.SetStateAction<FilterId[]>>;

  // Default AI search for trial users
  defaultAiSearchSet: boolean;
  setDefaultAiSearchSet: React.Dispatch<React.SetStateAction<boolean>>;

  // Trigger AI search from external components
  shouldTriggerOpenAiSearch: boolean;
  setShouldTriggerOpenAiSearch: React.Dispatch<React.SetStateAction<boolean>>;
}

const AiSearchContext = createContext<AiSearchContextType | null>(null);

export const AiSearchProvider = ({ children }: PropsWithChildren) => {
  const [aiSearchState, setAiSearchState] = useState<AiSearchState>({
    prompt: '',
    uuid: '',
    isCorrect: null,
    response: null,
  });

  const [isAiSearchLoading, setIsAiSearchLoading] = useState(false);
  const [shouldTriggerOpenAiSearch, setShouldTriggerOpenAiSearch] =
    useState(false);
  const [aiGeneratedFilterIds, setAiGeneratedFilterIds] = useState<FilterId[]>(
    []
  );
  const [defaultAiSearchSet, setDefaultAiSearchSet] = useState(false);

  const abortControllerRef = useRef<AbortController>();

  return (
    <AiSearchContext.Provider
      value={{
        aiSearchState,
        setAiSearchState,
        isAiSearchLoading,
        setIsAiSearchLoading,
        abortControllerRef,
        aiGeneratedFilterIds,
        setAiGeneratedFilterIds,
        defaultAiSearchSet,
        setDefaultAiSearchSet,
        shouldTriggerOpenAiSearch,
        setShouldTriggerOpenAiSearch,
      }}
    >
      {children}
    </AiSearchContext.Provider>
  );
};

export const useAiSearchContext = () => {
  const context = useContext(AiSearchContext);
  if (!context) {
    throw new Error(
      'useAiSearchContext must be used within an AiSearchProvider'
    );
  }
  return context;
};

export const useAiSearchContextSafe = () => {
  return useContext(AiSearchContext);
};
